import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TourItinerary = ({ itinerary }) => {
  const [expandedDays, setExpandedDays] = useState(new Set([0]));

  const toggleDay = (dayIndex) => {
    const newExpanded = new Set(expandedDays);
    if (newExpanded?.has(dayIndex)) {
      newExpanded?.delete(dayIndex);
    } else {
      newExpanded?.add(dayIndex);
    }
    setExpandedDays(newExpanded);
  };

  const expandAll = () => {
    setExpandedDays(new Set(itinerary.map((_, index) => index)));
  };

  const collapseAll = () => {
    setExpandedDays(new Set());
  };

  return (
    <div className="bg-white">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-foreground">Detailed Itinerary</h2>
        <div className="flex space-x-2">
          <button
            onClick={expandAll}
            className="text-sm text-primary hover:text-primary/80 transition-colors"
          >
            Expand All
          </button>
          <span className="text-muted-foreground">|</span>
          <button
            onClick={collapseAll}
            className="text-sm text-primary hover:text-primary/80 transition-colors"
          >
            Collapse All
          </button>
        </div>
      </div>
      <div className="space-y-4">
        {itinerary?.map((day, index) => (
          <div key={index} className="border border-border rounded-lg overflow-hidden">
            {/* Day Header */}
            <button
              onClick={() => toggleDay(index)}
              className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
                  {index + 1}
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-foreground">{day?.title}</h3>
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground mt-1">
                    <div className="flex items-center space-x-1">
                      <Icon name="Clock" size={14} />
                      <span>{day?.duration}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="MapPin" size={14} />
                      <span>{day?.locations?.length} locations</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Camera" size={14} />
                      <span>{day?.highlights?.length} highlights</span>
                    </div>
                  </div>
                </div>
              </div>
              <Icon
                name={expandedDays?.has(index) ? "ChevronUp" : "ChevronDown"}
                size={20}
                className="text-muted-foreground"
              />
            </button>

            {/* Day Content */}
            {expandedDays?.has(index) && (
              <div className="p-6 border-t border-border">
                {/* Description */}
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  {day?.description}
                </p>

                {/* Timeline */}
                <div className="space-y-6">
                  {day?.timeline?.map((activity, activityIndex) => (
                    <div key={activityIndex} className="flex space-x-4">
                      <div className="flex flex-col items-center">
                        <div className="w-3 h-3 bg-primary rounded-full"></div>
                        {activityIndex < day?.timeline?.length - 1 && (
                          <div className="w-px h-16 bg-border mt-2"></div>
                        )}
                      </div>
                      <div className="flex-1 pb-6">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-medium text-primary">{activity?.time}</span>
                          <span className="text-sm text-muted-foreground">•</span>
                          <span className="text-sm text-muted-foreground">{activity?.duration}</span>
                        </div>
                        <h4 className="font-semibold text-foreground mb-2">{activity?.title}</h4>
                        <p className="text-muted-foreground text-sm mb-3">{activity?.description}</p>
                        
                        {activity?.image && (
                          <div className="w-full h-48 rounded-lg overflow-hidden mb-3">
                            <Image
                              src={activity?.image}
                              alt={activity?.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}

                        {activity?.tips && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex items-start space-x-2">
                              <Icon name="Lightbulb" size={16} className="text-blue-600 mt-0.5" />
                              <div>
                                <div className="text-sm font-medium text-blue-900 mb-1">Pro Tip</div>
                                <div className="text-sm text-blue-700">{activity?.tips}</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Highlights */}
                <div className="mt-6 pt-6 border-t border-border">
                  <h4 className="font-semibold text-foreground mb-3">Day Highlights</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {day?.highlights?.map((highlight, highlightIndex) => (
                      <div key={highlightIndex} className="flex items-center space-x-2">
                        <Icon name="Check" size={16} className="text-success" />
                        <span className="text-sm text-muted-foreground">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Meals & Transportation */}
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {day?.meals && (
                      <div>
                        <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
                          <Icon name="Utensils" size={16} />
                          <span>Meals Included</span>
                        </h4>
                        <div className="space-y-2">
                          {day?.meals?.map((meal, mealIndex) => (
                            <div key={mealIndex} className="text-sm text-muted-foreground">
                              <span className="font-medium">{meal?.type}:</span> {meal?.description}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {day?.transportation && (
                      <div>
                        <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
                          <Icon name="Car" size={16} />
                          <span>Transportation</span>
                        </h4>
                        <div className="text-sm text-muted-foreground">
                          {day?.transportation}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Map Integration */}
      <div className="mt-8 p-6 bg-muted/30 rounded-lg">
        <h3 className="font-semibold text-foreground mb-4 flex items-center space-x-2">
          <Icon name="Map" size={18} />
          <span>Tour Route Map</span>
        </h3>
        <div className="w-full h-64 rounded-lg overflow-hidden">
          <iframe
            width="100%"
            height="100%"
            loading="lazy"
            title="Tour Route Map"
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps?q=25.2048,55.2708&z=12&output=embed"
            className="border-0"
          />
        </div>
      </div>
    </div>
  );
};

export default TourItinerary;